import { alpha } from '@mui/material/styles';
import { handleBreakpoints, type ResponsiveStyleValue } from '@mui/system';
import { REDUCED_MOTION_MEDIA_QUERY } from '@packages/shared-ui';
import { makeStyles } from '@/styles/theme/tss-react';
import { darkOrLightStyle } from '@/utils/styles';
import { InputSize } from './FormInput';

interface Props {
  isOnDark: boolean | undefined;
  inputSize: ResponsiveStyleValue<InputSize>;
}

const useStyles = makeStyles<Props>({ name: 'FormInput' })(
  ({ brandColors, type, typography: { pxToRem }, breakpoints }, props) => {
    const getInputSizeStyles = (size: InputSize) => {
      switch (size) {
        case InputSize.SM:
          return {
            padding: `${pxToRem(10)} ${pxToRem(16)}`,
            height: pxToRem(44), // fixed height since we don't always know line-height of font
          };
        case InputSize.LG:
          return {
            padding: pxToRem(16),
            height: pxToRem(56), // fixed height since we don't always know line-height of font
          };
        default:
          return {};
      }
    };

    return {
      wrapper: {
        flexBasis: '100%',
      },
      inputContainer: {
        width: '100%',
        marginRight: pxToRem(20),
        '&:last-child': {
          marginRight: 0,
        },
        position: 'relative',
        '&:has(input:disabled) label': {
          opacity: 0.4,
        },
      },
      responsiveSizes: handleBreakpoints(
        { theme: { breakpoints } },
        props.inputSize,
        (resolvedSize) => getInputSizeStyles(resolvedSize as InputSize)
      ),
      input: {
        width: '100%',
        borderRadius: pxToRem(8),
        fontSize: pxToRem(16),
        borderWidth: '1px',
        borderStyle: 'solid',
        outlineStyle: 'none',
        borderColor: alpha(brandColors.black, 0.3),
        colorScheme: 'light',
        color: brandColors.black,
        backgroundColor: brandColors.white,
        marginTop: pxToRem(6),
        '&:focus': {
          color: brandColors.tintsNeutral20,
        },
        '&:read-only': {
          color: brandColors.black,
          backgroundColor: brandColors.tintsHeroBlue90,
          borderColor: brandColors.tintsNeutral30,
        },
        '&:disabled': {
          opacity: 0.4,
        },
      },
      inputLabel: {
        display: 'flex',
        justifyItems: 'center',
        alighItems: 'center',
        ...type['14_medium'],
        letterSpacing: '-0.02rem',
        color: darkOrLightStyle(props)(
          brandColors.tintsOffWhite40,
          brandColors.tintsNeutral20
        ),
      },
      inputHoverActivity: {
        '&:hover:not(:focus):not(:disabled):not(:read-only)': {
          borderColor: brandColors.black,
        },
      },
      inputFocusActivity: {
        '&:focus': {
          borderWidth: pxToRem(1.5),
          borderColor: brandColors.tintsChargeBlue50,
        },
      },
      inputError: {
        paddingRight: pxToRem(52),
        transition: 'border-color .5s ease',
        [REDUCED_MOTION_MEDIA_QUERY]: {
          transition: 'none',
        },
        borderWidth: pxToRem(1.5),
        borderColor: darkOrLightStyle(props)(
          brandColors.semanticColorsError60,
          brandColors.semanticColorsError50
        ),
      },
      errorColor: {
        color: darkOrLightStyle(props)(
          brandColors.semanticColorsError60,
          brandColors.semanticColorsError50
        ),
      },
      helperText: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        ...type['14_medium'],
        lineHeight: pxToRem(20),
      },
      subtext: {
        color: brandColors.tintsNeutral30,
      },
      bottomTextContainer: {
        minHeight: pxToRem(20),
        marginTop: pxToRem(6),
      },
    };
  }
);

export default useStyles;
